import React, { useRef, useEffect, useState, useCallback, useMemo } from "react";
import ModalComponent from "../../components/ModalComponent";
import { FaUser, FaEdit, FaTimes, FaComment, FaLock } from "react-icons/fa";
import { sendCustomerNegotiationMessage, customerNegotiationMessageList } from "../../services/CommonService";

/* ---------------- ADD COMMENT MODAL ---------------- */
const Modal = ({ isOpen, onClose, selectedText, onSaveComment, editMode = false, initialComment = "" }) => {
  const [comment, setComment] = useState("");

  useEffect(() => {
    setComment(editMode ? initialComment || "" : "");
  }, [editMode, initialComment, isOpen]);

  const handleSave = () => {
    if (!comment.trim()) return;
    onSaveComment(comment);
    setComment("");
    onClose();
  };

  if (!isOpen) return null;

  return (
    <ModalComponent
      show={isOpen}
      handleClose={onClose}
      title={editMode ? "Edit Comment" : "Add Comment"}
      size="sm"
      footerButtons={[
        { label: "Cancel", variant: "secondary", onClick: onClose },
        { label: editMode ? "Update" : "Add", variant: "primary", onClick: handleSave, disabled: !comment.trim() },
      ]}
    >
      <div className="review-selected-text-box p-3">
        <p>{selectedText || "No text selected"}</p>
      </div>

      <div className="p-3">
        <textarea
          className="review-comment-input"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Add your comment..."
          rows={4}
          autoFocus
        />
      </div>
    </ModalComponent>
  );
};



/* ---------------- COMMENTS SIDEBAR ---------------- */
const CommentsSidebar = ({
  comments,
  onDeleteComment,
  onEditComment,
  onCommentHover,
  currentUserEmail,
  onCommentClick,
  isReadOnly = false,

  openReplyBox,
  setOpenReplyBox,

  commentReplies,
  setCommentReplies,

  replyInputs,
  setReplyInputs,

  loadingReplies,
  sendReply,
  loadReplies,

  formatReplyTime
}) => {
  return (
    <div className="comments-sidebar">
      <div className="sidebar-header-wrapper mb-2">
        <h3 className="sidebar-title">
          Comments
          {isReadOnly && (
            <span className="sidebar-badge">
              <FaLock className="badge-icon" />
              Read Only
            </span>
          )}
        </h3>
        <span className="comment-count">{comments.length}</span>
      </div>

      <div className="comments-list">
        {comments.length === 0 ? (
          <div className="no-comments-container">
            <FaComment className="no-comments-icon" />
            <p>No comments yet</p>
          </div>
        ) : (
          comments.map((comment) => {
            const isUserComment =
              comment.recipient_email === currentUserEmail || !comment.sent;
            const canEdit = !comment.sent && !isReadOnly;
            const canDelete = !comment.sent && !isReadOnly;

            return (
              <div
                key={comment.id}
                className={`comment-item ${isUserComment ? "user-comment" : "recipient-comment"
                  } ${comment.sent ? "sent-comment" : "draft-comment"}`}
                data-comment-id={comment.id}
                onMouseEnter={() => onCommentHover(comment.id, true)}
                onMouseLeave={() => onCommentHover(comment.id, false)}
                onClick={() => onCommentClick(comment)}
              >
                {/* Header */}
                <div className="comment-header-flex">
                  <div className="comment-author-wrapper">
                    <div
                      className={`author-avatar ${isUserComment ? "avatar-user" : "avatar-recipient"
                        }`}
                    >
                      <FaUser />
                    </div>
                    <div className="author-info">
                      <span className="author-name">
                        {comment.sent ? comment.recipient_name : "You"}
                      </span>

                      {comment.sent && comment.created_at && (
                        <span className="comment-timestamp">
                          {new Date(comment.created_at.replace("Z", "")).toLocaleString(
                            "en-IN",
                            {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                              hour12: true,
                            }
                          ).replace(",", " •")}
                        </span>
                      )}
                    </div>
                  </div>

                  {(canEdit || canDelete) && (
                    <div className="comment-actions-container">
                      {canEdit && (
                        <button
                          className="edit-comment-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            onEditComment(comment);
                          }}
                        >
                          <FaEdit />
                        </button>
                      )}

                      {canDelete && (
                        <button
                          className="delete-comment-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            onDeleteComment(comment.id);
                          }}
                        >
                          <FaTimes />
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {comment.selectedText && (
                  <div className="selected-text-preview">{comment.selectedText}</div>
                )}

                <div className="comment-text">{comment.comment_text}</div>

                <div className="comment-footer">
                  {/* ====== REPLY SECTION ====== */}
                  <div className="reply-section">
                    <div className="d-flex justify-content-between" style={{ alignItems: "baseline" }}>
                      {/* Toggle Button - Only show for sent comments */}
                      <div>
                        {comment.sent && (
                          <button
                            className="reply-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              const isOpen = !!openReplyBox[comment.id];
                              setOpenReplyBox(prev => ({ ...prev, [comment.id]: !isOpen }));
                              if (!isOpen) loadReplies(comment.id);
                            }}
                          >
                            <i class="bi bi-chat-dots"></i>  {openReplyBox[comment.id] ? "Replies" : "Replies"}
                          </button>
                        )}
                      </div>

                      <div>
                        {comment.sent ? (
                          <span className="review-status-badge sent-badge">Sent</span>
                        ) : (
                          <span className="review-status-badge draft-badge">Draft</span>
                        )}
                      </div>
                    </div>

                    {openReplyBox[comment.id] && (
                      <div className="reply-thread">
                        <div className="reply-list">
                          {commentReplies[comment.id]?.map((reply) => (
                            <div key={reply.id} className="reply-item">
                              <div className="reply-bubble">
                                <div className="reply-top-row d-block">
                                <span className="reply-sender">{reply.sender_name}</span>
                                  <div className="reply-time d-block">
                                    {formatReplyTime(reply.created_at)}
                                  </div>
                                </div>
                                <div className="reply-message">{reply.message}</div>
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Reply Input */}
                        <div className="reply-input-row">
                          <input
                            type="text"
                            placeholder="Write a reply..."
                            value={replyInputs[comment.id] || ""}
                            onChange={(e) =>
                              setReplyInputs((prev) => ({
                                ...prev,
                                [comment.id]: e.target.value,
                              }))
                            }
                            className="reply-input"
                          />
                          <button className="send-reply-btn" onClick={() => sendReply(comment.id)}>
                            Send
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};



/* ---------------- GLOBAL TEXT OFFSET ---------------- */
const getGlobalOffsets = (range, rootElement) => {
  if (!range || !rootElement) return { startOffset: 0, endOffset: 0 };

  try {
    const walker = document.createTreeWalker(rootElement, NodeFilter.SHOW_TEXT);
    const textNodes = [];
    let currentNode;

    while ((currentNode = walker.nextNode())) textNodes.push(currentNode);

    let nonWhitespaceCount = 0;
    let startOffset = 0;
    let endOffset = 0;

    textNodes.forEach((node) => {
      const text = node.textContent;

      if (node === range.startContainer) {
        startOffset = nonWhitespaceCount + text.substring(0, range.startOffset).replace(/\s/g, "").length;
      }

      if (node === range.endContainer) {
        endOffset = nonWhitespaceCount + text.substring(0, range.endOffset).replace(/\s/g, "").length;
      }

      nonWhitespaceCount += text.replace(/\s/g, "").length;
    });

    return { startOffset, endOffset };
  } catch {
    return { startOffset: 0, endOffset: 0 };
  }
};



/* ======================== MAIN COMPONENT ======================== */
const NegotiationContent = ({
  htmlContent,
  currentUserEmail,
  apiComments = [],
  isReadOnly = false,
  onCommentsUpdate,
  onNewCommentAdded,
  clearDraftCommentsRef,
}) => {
  const contentRef = useRef(null);

  const [localComments, setLocalComments] = useState([]);

  const [commentReplies, setCommentReplies] = useState({});
  const [replyInputs, setReplyInputs] = useState({});
  const [openReplyBox, setOpenReplyBox] = useState({});
  const [loadingReplies, setLoadingReplies] = useState({});

  const [selectedText, setSelectedText] = useState("");
  const [selectionRange, setSelectionRange] = useState(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingComment, setEditingComment] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);

  const [showButton, setShowButton] = useState(false);
  const [buttonPosition, setButtonPosition] = useState({ top: 0, left: 0 });



  /* ---------------------- MERGE COMMENTS ---------------------- */
  const allComments = useMemo(() => {
    const draftOnly = localComments.filter(
      (local) =>
        !apiComments.some(
          (api) =>
            api.position?.start_offset === local.position?.start_offset &&
            api.position?.end_offset === local.position?.end_offset &&
            api.comment_text === local.comment_text
        )
    );
    return [...apiComments, ...draftOnly];
  }, [apiComments, localComments]);



  /* ---------------------- CLEAR DRAFTS ---------------------- */
  useEffect(() => {
    if (clearDraftCommentsRef)
      clearDraftCommentsRef.current = () => setLocalComments([]);
  }, [clearDraftCommentsRef]);



  /* ---------------------- HIGHLIGHT COMMENTS ---------------------- */
  const applyHighlights = useCallback(() => {
    if (!contentRef.current) return;

    const root = contentRef.current;

    // remove old highlights
    root.querySelectorAll(".highlighted-text").forEach((el) => {
      const parent = el.parentNode;
      while (el.firstChild) parent.insertBefore(el.firstChild, el);
      parent.removeChild(el);
    });

    allComments.forEach((c) => {
      const { start_offset, end_offset } = c.position || {};
      if (start_offset == null || end_offset == null) return;

      let nonWhitespaceCount = 0;
      let startNode = null,
        endNode = null;
      let startPos = 0,
        endPos = 0;

      const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
      let node;

      while ((node = walker.nextNode())) {
        const text = node.textContent;

        for (let i = 0; i < text.length; i++) {
          if (!/\s/.test(text[i])) {
            if (nonWhitespaceCount === start_offset) {
              startNode = node;
              startPos = i;
            }
            if (nonWhitespaceCount === end_offset) {
              endNode = node;
              endPos = i;
            }
            nonWhitespaceCount++;
          }
        }

        if (startNode && endNode) break;
      }

      if (!startNode || !endNode) return;

      const range = document.createRange();
      range.setStart(startNode, startPos);
      range.setEnd(endNode, endPos);

      const span = document.createElement("span");
      span.className = "highlighted-text";
      span.dataset.commentId = c.id;

      span.appendChild(range.cloneContents());
      range.deleteContents();
      range.insertNode(span);
    });
  }, [allComments]);

  useEffect(() => applyHighlights(), [applyHighlights]);



  /* ---------------------- UPDATE PARENT ---------------------- */
  useEffect(() => onCommentsUpdate?.(localComments), [localComments]);



  /* ---------------------- TEXT SELECTION HANDLER ---------------------- */
  useEffect(() => {
    if (isReadOnly) return;

    const handleMouseUp = () => {
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0) return;

      const range = sel.getRangeAt(0);
      const selected = sel.toString().trim();

      if (!selected) {
        setShowButton(false);
        return;
      }

      const rect = range.getBoundingClientRect();
      const container = contentRef.current.getBoundingClientRect();

      setSelectedText(selected);
      setSelectionRange(range);

      setButtonPosition({
        top: rect.top - container.top - 40,
        left: "30%",
      });

      setShowButton(true);
    };

    document.addEventListener("mouseup", handleMouseUp);
    return () => document.removeEventListener("mouseup", handleMouseUp);
  }, [isReadOnly]);



  /* ---------------------- SAVE COMMENT ---------------------- */
  /* Save comment (send exact selected text in payload) */
  const handleSaveComment = (text) => {
    if (!selectedText || !selectionRange) return;

    // Get global position offsets
    const { startOffset, endOffset } = getGlobalOffsets(selectionRange, contentRef.current);

    // If editing existing comment
    if (isEditMode && editingComment) {
      setLocalComments((prev) =>
        prev.map((c) =>
          c.id === editingComment.id
            ? { ...c, comment_text: text, selectedText }
            : c
        )
      );
    } else {
      const newComment = {
        id: `comment-${Date.now()}`,
        comment_text: text,

        position: { start_offset: startOffset, end_offset: endOffset, selectedText },
        sent: false,
        recipient_email: currentUserEmail,
      };

      setLocalComments((prev) => [...prev, newComment]);
      onNewCommentAdded?.();
    }

    // Reset modal & states
    setIsModalOpen(false);
    setIsEditMode(false);
    setEditingComment(null);
  };




  /* ---------------------- EDIT + DELETE ---------------------- */
  const handleEditComment = useCallback((comment) => {
    setEditingComment(comment);
    setSelectedText(comment.selectedText);
    setIsEditMode(true);
    setIsModalOpen(true);
  }, []);

  const handleDeleteComment = useCallback(
    (id) => setLocalComments((prev) => prev.filter((c) => c.id !== id)),
    []
  );



  /* ---------------------- HOVER HIGHLIGHT ---------------------- */
  const handleCommentHover = useCallback((id, hover) => {
    contentRef.current
      ?.querySelectorAll(`[data-comment-id="${id}"]`)
      ?.forEach((el) => {
        el.style.backgroundColor = hover
          ? "rgba(255,193,7,0.8)"
          : "rgba(255,235,59,0.5)";
      });
  }, []);



  /* ---------------------- CLICK SCROLL ---------------------- */
  const handleCommentClick = useCallback((comment) => {
    const target = contentRef.current?.querySelector(
      `[data-comment-id="${comment.id}"]`
    );
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "center" });
      target.classList.add("highlight-active-click");
      setTimeout(() => target.classList.remove("highlight-active-click"), 1500);
    }
  }, []);



  /* ---------------------- CLOSE MODAL ---------------------- */
  const handleModalClose = () => {
    setIsModalOpen(false);
    setIsEditMode(false);
    setEditingComment(null);
  };
  const negotiationUUID = window?.location?.pathname?.split("/")?.pop();


  const loadReplies = async (commentId) => {
    try {
      setLoadingReplies(prev => ({ ...prev, [commentId]: true }));

      const apiComment = apiComments.find(c => c.id === commentId);

      if (!apiComment) {
        console.warn("Replies NOT allowed for draft comments");
        setCommentReplies(prev => ({ ...prev, [commentId]: [] }));
        return;
      }

      const numericId = parseInt(apiComment.id.toString().replace(/\D/g, ""), 10);

      const res = await customerNegotiationMessageList({
        comment_id: numericId,
        uuid: negotiationUUID
      });

      setCommentReplies(prev => ({
        ...prev,
        [commentId]: res?.data?.data || []
      }));

    } catch (err) {
      console.error("Reply load error:", err);
    } finally {
      setLoadingReplies(prev => ({ ...prev, [commentId]: false }));
    }
  };


  const sendReply = async (commentId) => {
    const text = replyInputs[commentId];
    if (!text?.trim()) return;

    const apiComment = apiComments.find(c => c.id === commentId);
    if (!apiComment) {
      console.error("Reply NOT allowed on draft comment");
      return;
    }

    const numericId = parseInt(apiComment.id.toString().replace(/\D/g, ""), 10);
    const cleanUUID = negotiationUUID?.split("/")?.pop()?.trim();

    await sendCustomerNegotiationMessage({
      comment_id: numericId,
      message_text: text,
      uuid: cleanUUID
    });

    setReplyInputs(prev => ({ ...prev, [commentId]: "" }));

    loadReplies(commentId);
  };

  const formatReplyTime = (isoString) => {
    if (!isoString) return "";
  
    const date = new Date(isoString);
  
    return date.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    }).replace(",", " •");
  };
  

  return (
    <>
      <div className="negotiation-container-review two-pane-layout">

        {/* Render Document HTML */}
        <div
          ref={contentRef}
          className="negotiation-content"
          dangerouslySetInnerHTML={{ __html: htmlContent }}
          style={{ userSelect: isReadOnly ? "none" : "text" }}
        />

        {/* Comments Sidebar */}
        <CommentsSidebar
          comments={allComments}
          onCommentClick={handleCommentClick}
          onDeleteComment={handleDeleteComment}
          onEditComment={handleEditComment}
          onCommentHover={handleCommentHover}
          currentUserEmail={currentUserEmail}
          isReadOnly={isReadOnly}

          openReplyBox={openReplyBox}
          setOpenReplyBox={setOpenReplyBox}

          commentReplies={commentReplies}
          setCommentReplies={setCommentReplies}

          replyInputs={replyInputs}
          setReplyInputs={setReplyInputs}

          loadingReplies={loadingReplies}
          sendReply={sendReply}
          loadReplies={loadReplies}

          formatReplyTime={formatReplyTime}
        />



        {/* Add Comment Floating Button */}
        {showButton && !isReadOnly && (
          <button
            className="add-comment-button"
            style={{
              top: `${buttonPosition.top}px`,
              left: buttonPosition.left,
            }}
            onClick={() => setIsModalOpen(true)}
          >
            <FaComment /> Add Comment
          </button>
        )}

        {/* Modal */}
        <Modal
          isOpen={isModalOpen}
          onClose={handleModalClose}
          selectedText={selectedText}
          onSaveComment={handleSaveComment}
          editMode={isEditMode}
          initialComment={editingComment?.comment_text || ""}
        />
      </div>
    </>
  );
};

export default NegotiationContent;
